import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Form, Popconfirm, Row } from 'antd';

import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { setAppLoader, setWarningModal } from '../../../../../../../redux/common/CommonActions';
import { calCulateFreightClass, validateProductForCalculateClass } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import { freightClassCalculator } from '../../../../../../../utilz/shipmentDataManupulation';
import Button from '../../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../../components/Labels/PrimaryLabel';
import TMSProductDetailsCard from './TMSProductDetailsCard';

const TMSProductDetails = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const formItems = Form.useWatch('items', form);
  const [itemsData, setItemsData] = useState([]);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleDataChange = (data, index) => {
    const itemsLocal = form.getFieldValue('items');
    itemsLocal[index] = { ...itemsLocal[index], ...data };
    form.setFieldValue('items', itemsLocal);
  };

  const handleProductClassError = async (items) => {
    try {
      await validateProductForCalculateClass(items);
    } catch (description) {
      dispatch(setWarningModal({ description, open: true }));
    }
  };

  const handleSubmit = async (data, index) => {
    try {
      await form.validateFields();
      const itemsLocal = form.getFieldValue('items');
      handleProductClassError(itemsLocal);
      itemsLocal[index] = {
        ...itemsLocal[index],
        ...data,
        class:
          data?.class > 0
            ? data?.class
            : calCulateFreightClass(
                data?.length,
                data?.width,
                data?.height,
                data?.weight,
                data?.weightUom,
                data?.billQty,
                data?.weight,
                data?.dimUom,
                data?.cube,
                data?.isCmsAllowed,
              ),
      };
      form.setFieldValue('items', itemsLocal);
      setItemsData(itemsLocal);
    } catch {}
  };

  const isEditing = formItems?.map(({ editMode }) => Boolean(editMode))?.includes(true);

  const selectedItemsIndex = formItems
    ?.map(({ selected }, index) => (selected ? index : null))
    ?.filter((val) => val !== null);

  const handleFreightClassCalculator = async (productList) => {
    try {
      await form.validateFields();
      dispatch(setAppLoader(true));
      form.setFieldValue(
        'items',
        productList.map((row) => ({ ...row, class: '0' })),
      );
      const productListLocal = await freightClassCalculator(productList);
      form.setFieldValue('items', productListLocal);
      dispatch(setAppLoader(false));
      showNotification({
        message: t('translation:shipment.freightClassSuccessfully'),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    } catch (error) {
      let errorMessage = error;
      dispatch(setAppLoader(false));
      if (error.errorFields) {
        const errorsList = error.errorFields.map(({ errors }) => errors[0]);
        errorMessage = errorsList?.join(', ');
      }
      dispatch(
        setWarningModal({
          description: `${errorMessage}.`,
          open: true,
        }),
      );
    }
  };

  return (
    <Form
      name="productForm"
      form={form}
      initialValues={{
        items: [
          {
            billQtyUom: 'PT',
            createdBy: user?.data?.id,
            dimUom: 'N',
            editMode: true,
            gridOperation: 1,
            isNew: true,
            itemSequenceKey: 1,
            weightUom: 'LB',
          },
        ],
      }}
    >
      <Form.List name="items">
        {(items, { add, remove, move }) => (
          <>
            <Row justify="space-between" align="middle">
              <Col>
                <PrimaryLabel>{t('translation:getQuote.productDetails.productInformation')}</PrimaryLabel>
              </Col>
              <Col>
                <Row gutter={15}>
                  <Col>
                    <Button
                      onClick={() => {
                        add({
                          billQtyUom: 'PT',
                          createdBy: user?.data?.id,
                          dimUom: 'N',
                          editMode: true,
                          gridOperation: 1,
                          isNew: true,
                          itemSequenceKey: 1,
                          weightUom: 'LB',
                        });
                        move(items.length, 0);
                      }}
                      disabled={isEditing}
                    >
                      {t('translation:default.buttons.addProduct')}
                    </Button>
                  </Col>
                  <Col>
                    <Popconfirm
                      placement="bottomRight"
                      title={t('translation:default.fields.popconfirmFreightClass')}
                      onConfirm={() => handleFreightClassCalculator(form.getFieldValue('items'))}
                      okText={t('translation:default.options.yes')}
                      cancelText={t('translation:default.options.no')}
                      overlayClassName="delete-popup-container w-auto"
                    >
                      <Button disabled={items.length === 0}>{t('translation:shipment.freightClassCalculator')}</Button>
                    </Popconfirm>
                  </Col>
                  <Col>
                    <Popconfirm
                      placement="topRight"
                      title={t('translation:default.fields.modal.label')}
                      onConfirm={() => remove(selectedItemsIndex)}
                      okText={t('translation:default.fields.modal.okText')}
                      cancelText={t('translation:default.options.no')}
                      overlayClassName="delete-popup-container"
                    >
                      <Button variant="danger" type="default" disabled={selectedItemsIndex?.length === 0}>
                        {t('translation:default.buttons.deleteProduct')}
                      </Button>
                    </Popconfirm>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider className="border-top-border my-15" style={{ marginLeft: '-10%', width: '120%' }} />
            <div>
              {items.map(({ name }, index) => (
                <TMSProductDetailsCard
                  key={name}
                  name={name}
                  index={index}
                  remove={remove}
                  itemDetails={formItems?.[name]}
                  handleDataChange={handleDataChange}
                  handleSubmit={handleSubmit}
                  defaultValues={itemsData?.[name]}
                  isLast={index + 1 === items.length}
                />
              ))}
            </div>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default TMSProductDetails;
